/* istanbul ignore file */
import { PM_PROVIDERS } from 'common/constants/constants';
import { FILTER_TYPES } from 'common/constants/filters';
import { usePmFiltersBuilder } from 'components/FilterMenus/pm.filters';
import { useContext, useEffect } from 'react';
import { omit, omitBy, isEqual } from 'lodash';
import useEventsTracker from '../tracking/useEventsTracker';
import useTriggerFilterSetCTA from './SaveFilterSetCTA/useTriggerFilterSetCTA';
import useIsTreeHierarchyEnabled from '../../../hooks/useIsTreeHierarchyEnabled';
import useContextActions from '../../../useContextActions';
import useFF from 'common/hooks/settings/useFF';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';

const separatorStyle = {
  borderTop: `2px solid #ccc`,
  margin: '0.5rem 0',
  padding: '0.5rem 0'
};

const useFilters = ({ Context }) => {
  const {
    state: { filters, localFilters }
  } = useContext(Context);
  const contextActions = useContextActions({ Context });
  const eventTracker = useEventsTracker({ Context });
  const triggerFilterSetCTA = useTriggerFilterSetCTA(Context);
  const isTreeHierarchyEnabled = useIsTreeHierarchyEnabled(Context);
  const isLinearBTeamProjectFiltersEnabled = useFF(
    FEATURE_FLAGS_NAMES.isLinearBTeamProjectFiltersEnabled
  );

  const menuItems = usePmFiltersBuilder({
    [PM_PROVIDERS.JIRA]: [
      FILTER_TYPES.PROJECTS,
      FILTER_TYPES.BOARD,
      ...(isTreeHierarchyEnabled
        ? [FILTER_TYPES.DYNAMIC_ISSUES]
        : [FILTER_TYPES.ISSUE_PARENT_2, FILTER_TYPES.ISSUE_PARENT_1]),
      FILTER_TYPES.LABEL,
      FILTER_TYPES.FIELD,
      FILTER_TYPES.FIX_VERSION,
      ...(isLinearBTeamProjectFiltersEnabled
        ? [FILTER_TYPES.ORGANIZATION_TEAMS]
        : [])
    ],
    [PM_PROVIDERS.AZURE_BOARDS]: [
      FILTER_TYPES.PROJECTS,
      ...(isTreeHierarchyEnabled
        ? [FILTER_TYPES.DYNAMIC_ISSUES]
        : [FILTER_TYPES.ISSUE_PARENT_2, FILTER_TYPES.ISSUE_PARENT_1]),
      FILTER_TYPES.LABEL,
      FILTER_TYPES.FIELD
    ]
  })
    .config(PM_PROVIDERS.JIRA, FILTER_TYPES.PROJECTS, {
      title: 'Project'
    })
    .config(PM_PROVIDERS.JIRA, FILTER_TYPES.BOARD, {
      title: 'Board'
    })
    .config(PM_PROVIDERS.JIRA, FILTER_TYPES.ORGANIZATION_TEAMS, {
      title: `LinearB Team Scope`,
      style: separatorStyle
    })
    .config(PM_PROVIDERS.AZURE_BOARDS, FILTER_TYPES.PROJECTS, {
      title: 'Project'
    });

  const onChange = updatedFilters => {
    const verifiedFilters = omitBy(
      updatedFilters,
      values => values?.length === 0
    );
    contextActions.onChangeLocalFilters(verifiedFilters);
  };
  const onApply = () => {
    eventTracker.onApplyFilters();
    if (triggerFilterSetCTA.shouldShowSaveFilterSetCTA())
      return triggerFilterSetCTA.triggerModal();

    contextActions.onChangeFilters(localFilters);
  };

  const hasFiltersChanged = !isEqual(filters, localFilters);

  const onDelete = (filterType, id) => {
    const currentSelectedFilters = localFilters?.[filterType] || [];
    const newSelectedFilters = currentSelectedFilters.filter(
      item => item.id !== id
    );
    const filtersAfterDelete = {
      ...omit(localFilters, filterType),
      ...(newSelectedFilters.length ? { [filterType]: newSelectedFilters } : {})
    };
    contextActions.onChangeLocalFilters(filtersAfterDelete);
  };

  useEffect(() => {
    contextActions.onChangeLocalFilters(filters);
  }, [filters]);

  return {
    localFilters,
    menuItems,
    onChange,
    onApply,
    onDelete,
    hasFiltersChanged
  };
};

export default useFilters;
